/**
 * Read-Only Contract Display Component
 *
 * Displays a contract instance in read-only mode for review.
 * Used by ContractReviewModal to show signed contracts.
 *
 * Features:
 * - Renders all contract sections with interpolated variables
 * - Shows LO signature if present
 * - Shows company signature placeholder
 * - No editing capability
 */

import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ContractInstanceData } from '@/types/recruitment';
import { SectionRenderer } from './ContractSectionRenderers';
import { interpolateVariables, computeDerivedVariables } from './contract-utils';
import { parseAsLocalDate } from '@/utils/dateHelpers';

interface ReadOnlyContractDisplayProps {
  contractInstance: ContractInstanceData;
  showSignatures?: boolean;
}

export const ReadOnlyContractDisplay: React.FC<ReadOnlyContractDisplayProps> = ({
  contractInstance,
  showSignatures = true
}) => {
  // Compute derived variables (like dates, calculations)
  const variableData = computeDerivedVariables(contractInstance.variableData);

  // Get sections from contract instance
  const sections = contractInstance.sections || [];

  return (
    <div className="space-y-6">
      {/* Contract Header */}
      <div className="border-b pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">
              {variableData.firstName} {variableData.lastName} - Employment Contract
            </h3>
            <p className="text-sm text-muted-foreground">
              Version {contractInstance.versionNumber || 1}
            </p>
          </div>
          <Badge variant={contractInstance.status === 'signed' ? 'success' : 'secondary'}>
            {contractInstance.status === 'recruit_signed' ? 'LO Signed' :
             contractInstance.status === 'signed' ? 'Fully Signed' :
             contractInstance.status.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Contract Sections */}
      <div className="space-y-6">
        {sections.map((section: any, index: number) => {
          return (
            <Card key={section.id || index} className="p-6">
              <SectionRenderer
                section={section}
                variableData={variableData}
                interpolate={interpolateVariables}
                checkboxStates={contractInstance.checkboxStates || {}}
                onCheckboxChange={() => {}}  // No-op for read-only
                readOnly={true}
              />
            </Card>
          );
        })}
      </div>

      {/* Signatures Section */}
      {showSignatures && (
        <div className="space-y-6 border-t pt-6">
          {/* Loan Officer Signature */}
          <Card className="p-6">
            <h4 className="font-semibold mb-4">Loan Officer Signature</h4>
            {contractInstance.hasLoSignature && contractInstance.loanOfficerSignatureDataUrl ? (
              <div className="space-y-3">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img
                    src={contractInstance.loanOfficerSignatureDataUrl}
                    alt="Loan Officer Signature"
                    className="max-w-xs h-auto"
                  />
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>
                    <span className="font-medium">Signed:</span>{' '}
                    {contractInstance.loanOfficerSignatureDate
                      ? parseAsLocalDate(contractInstance.loanOfficerSignatureDate).toLocaleString()
                      : 'Date not available'}
                  </p>
                  <p>
                    <span className="font-medium">Method:</span>{' '}
                    {contractInstance.loanOfficerSignatureMethod === 'drawn'
                      ? 'Hand-drawn'
                      : contractInstance.loanOfficerSignatureMethod === 'typed'
                      ? 'Typed'
                      : 'Uploaded'}
                  </p>
                  {contractInstance.loanOfficerSignatureIpAddress && (
                    <p>
                      <span className="font-medium">IP Address:</span>{' '}
                      {contractInstance.loanOfficerSignatureIpAddress}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-8 bg-gray-50 text-center text-muted-foreground">
                <p>Loan Officer signature pending</p>
              </div>
            )}
          </Card>

          {/* Company Signature */}
          <Card className="p-6">
            <h4 className="font-semibold mb-4">Company Representative Signature</h4>
            {contractInstance.hasRecruiterSignature && contractInstance.recruiterSignatureDataUrl ? (
              <div className="space-y-3">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img
                    src={contractInstance.recruiterSignatureDataUrl}
                    alt="Company Representative Signature"
                    className="max-w-xs h-auto"
                  />
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>
                    <span className="font-medium">Signed:</span>{' '}
                    {contractInstance.recruiterSignatureDate
                      ? parseAsLocalDate(contractInstance.recruiterSignatureDate).toLocaleString()
                      : 'Date not available'}
                  </p>
                  <p>
                    <span className="font-medium">Method:</span>{' '}
                    {contractInstance.recruiterSignatureMethod === 'drawn'
                      ? 'Hand-drawn'
                      : contractInstance.recruiterSignatureMethod === 'typed'
                      ? 'Typed'
                      : 'Uploaded'}
                  </p>
                  {contractInstance.recruiterSignatureIpAddress && (
                    <p>
                      <span className="font-medium">IP Address:</span>{' '}
                      {contractInstance.recruiterSignatureIpAddress}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-8 bg-gray-50 text-center text-muted-foreground">
                <p>Company representative signature pending</p>
                <p className="text-xs mt-2">
                  Contract must be signed by authorized representative
                </p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* E-SIGN Compliance Footer */}
      <div className="border-t pt-4 text-xs text-muted-foreground">
        <p>
          This contract is electronically signed and complies with the U.S. ESIGN Act and UETA.
          All signatures are legally binding and enforceable.
        </p>
      </div>
    </div>
  );
};
